<template>
  <v-layout class="mx-4" column id="triage">
    <div v-if="loading" class="text-center ma-3">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    
    <div v-else>
      <div v-if="!valid">
        <v-card class="ma-2">
          <v-card-text class="text-center">
            <h3 class="pb-2"><mrx-translation :token="'app.device.list.location_check.error'"></mrx-translation></h3>
            <p><mrx-translation :token="'app.error.instructions.title'"></mrx-translation></p>
            <p v-show="notify"><mrx-translation :token="message"></mrx-translation></p>
            <br/>
            <v-btn id="return-to-track" color="success" @click="backToTrack">
              <mrx-translation :token="'app.end.new_assessment_tracks.button'"></mrx-translation>
            </v-btn>
          </v-card-text>
        </v-card>
      </div>

      <div v-else>
        <v-dialog
          v-model="dialog"
          scrollable
          persistent
          overlay-color=primaryblue
          overlay-opacity="0.9"
          max-width="600"
        >
          <v-card
            class="intro-dark"
            align="center"
            style="border-radius: 4px">
            <mrx-triage spacingClass="mb-0" borderRadius="4px" bodyClass="intro-dark" instructions>
            </mrx-triage>
            <v-card-actions class="justify-center mb-3">
              <v-btn
                id="severity-consent"
                class="consent"
                @click="dialog = false"
              >
                <mrx-translation token="severities.warning.agreement"></mrx-translation>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <mrx-triage></mrx-triage>

        <v-card v-if="showInstructions" id="extra-instructions" flat color="white" class="mb-2">
          <v-card-text class="assessment-light" style="font-size: 16px;">
            <mrx-translation token="app.triage.instructions.title"></mrx-translation>
          </v-card-text>
        </v-card>

        <v-layout column class="conditions">
          <template v-for="triage in triages">
            <post-assessment-card
              v-if="anyConditionsMatchTriage(triage)"
              :isChecklist="showCheckboxes"
              :headerToken = "`triage.${triage}.title`"
              :headerIcon="icon(triage).icon"
              :headerIconColor="icon(triage).color"
              :key="triage"
              :contentList="formatTriages(triage)"
              :isExpandable="true"
              :defaultExpanded="isExpanded(triage)"
              v-model="selected"
              @handleInput="setSelected"
            >
          </post-assessment-card>
        </template>
      </v-layout>
    </div>
  </div>
</v-layout>
</template>

<script>
  import languageLogic from '@/mixins/languageLogic'
  import mrx from '@/mixins/mrx'
  import { mapGetters } from 'vuex'
  import icons from '@/mixins/icons'
  import PostAssessmentCard from '../layouts/PostAssessmentCard.vue'
  import {savePatientVisitInsights} from '../../utils/patientVisitInsightsUtils'

  export default {
    name: 'Triage',
    mixins: [languageLogic, mrx, icons],
    components: { PostAssessmentCard },
    data() {
      return {
        loading: true,
        valid: null,
        overallTriage: 'none',
        conditions: [],
        dialog: true,
        selected: [],
      }
    },
    computed: {
      message() {
        return `app.notifications.${this.notification}.title`
      },
      notify() {
        return this.notification !== null
      },
      showCheckboxes() {
        return !this.panel.options?.hideCheckboxes
      },
      showInstructions() {
        return this.panel.options?.showInstructions
      },
      ...mapGetters({
        notification: 'v4/mrx/notification',
        validAssessment: 'v4/mrx/valid',
        panel: 'v4/panel/current',
        triageFromBundle: 'v4/mrx/triage',
        filterConditions: 'v4/track/filterConditions',
        currentTrack: 'v4/track/current',
      })
    },
    mounted() {
      this.assess()
    },
    async beforeRouteLeave(to, from, next) {
      if (this.valid && this.conditions.length > 0 && this.selected.length === 0) {
        this.$track(this.$analyticsEvents.TRIAGE_NO_SELECTION, {
          track_name: this.currentTrack?.name,
          overall_triage: this.overallTriage,
          available_conditions: this.conditions.length,
          available_urgent: this.conditionsWithTriageMatch('urgent').length,
          available_immediate: this.conditionsWithTriageMatch('immediate').length,
          available_standard: this.conditionsWithTriageMatch('standard').length,
          available_inconclusive: this.conditionsWithTriageMatch('inconclusive').length,
        })
      }
      next()
    },
    methods: {
      async assess() {
        let assessedEncounter
        if (this.filterConditions.length > 0) {
          assessedEncounter = await this.$store.dispatch('v4/mrx/assess', {
            conditions: this.filterConditions
          })
        } else {
          assessedEncounter = await this.$store.dispatch('v4/mrx/assess')
        }
        if (this.validAssessment) {
          this.valid = true
          this.conditions = assessedEncounter
              .filter(ae => ae.type === 'assessment' && !ae.display_hidden)
          this.overallTriage = this.triageFromBundle
          this.selected = []

          this.$track(this.$analyticsEvents.TRIAGE_PANEL_VIEWED, {
            track_name: this.currentTrack?.name,
            overall_triage: this.overallTriage,
            total_conditions: this.conditions.length,
            conditions_urgent: this.conditionsWithTriageMatch('urgent').length,
            conditions_immediate: this.conditionsWithTriageMatch('immediate').length,
            conditions_standard: this.conditionsWithTriageMatch('standard').length,
            conditions_inconclusive: this.conditionsWithTriageMatch('inconclusive').length,
            conditions_none: this.conditionsWithTriageMatch('none').length,
          })
        }
        else {
          this.$store.dispatch('v4/collect/send')
        }
        await this.savePatientVisitInsightsData()
        this.loading = false
      },
      backToTrack() {
        this.$router.push({ name: 'Track' })
      },
      formatTriages(triage) {
        const conditionsWithTriage = this.conditionsWithTriageMatch(triage)
        return conditionsWithTriage.map(condition => ({
          name: condition.name,
          headerToken: this.riskAssessmentToken(condition.name, condition.severity)
        }))
      },
      conditionsWithTriageMatch(triage) {
        return this.conditions.filter(condition =>
          !condition.display_hidden &&
          condition.triage_recommendation === triage
          && condition.severity !== "not_assessed"
        );
      },
      anyConditionsMatchTriage(triage) {
        return this.conditionsWithTriageMatch(triage).length > 0
      },
      isExpanded(triage) {
        return triage !== 'none' || this.overallTriage === 'none'
      },
      setSelected(selected) {
        this.selected = selected
      },
      async savePatientVisitInsightsData() {
        const categoriesData = await this.$store.dispatch('v4/mrx/conditions')
        if (!categoriesData
            || categoriesData.length === 0
            || !this.conditions
            || this.conditions.length === 0) {
          return;
        }

        const sessionDetails = this.$store.getters['v4/session/details']
        const currentTrack = this.$store.getters['v4/track/current']
        let selectedTriage = this.$store.getters['v4/mrx/triageSelected']()

        if (selectedTriage === 'none_selected') {
          selectedTriage = this.overallTriage
        }

        await savePatientVisitInsights(
            this.conditions?.filter(condition => condition.severity !== "not_assessed"),
            categoriesData,
            currentTrack,
            sessionDetails,
            selectedTriage,
            this.selected)
      },
    },
    watch: {
      selected: function() {
        this.$store.commit('v4/mrx/select', { type: 'assessment', data: this.selected })
        this.$store.commit('v4/mrx/updateCareplanShownList', this.selected)
        void this.savePatientVisitInsightsData().catch(() => {})
      },
    },
  }
</script>

<style scoped>
  #triage {
    margin-bottom: 20px;
  }

  .consent {
    color: var(--primary-blue) !important;
    background-color: white !important;
    text-transform: uppercase;
    font-weight: 600;
  }
</style>
